export interface DocumentFormValues {
  nd_number: string;
  em_number: string;
  title: string;
  work_package_id: string;
  work_unit_id: string;
  document_type_id: string;
  discipline_id: string;
  notes: string;
  target_value: string;
  qualifier: string;
  communication_user_ids: number[];
  communication_include_project_members: boolean;
}

export interface RevisionFormValues {
  revision_name_id: number;
  /** Null while the step field is cleared during typing. */
  step: number | null;
  start_date: string;
  expected_issue_date: string;
}

export interface RevisionWorkflowFormValues {
  author_party_id?: number;
  checkers: Array<{ party_id: number; step_order: number }>;
  approvers: Array<{ party_id: number; step_order: number }>;
}

export const getTodayDateString = (): string => {
  const now = new Date();
  const timezoneOffsetMs = now.getTimezoneOffset() * 60 * 1000;
  return new Date(now.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

export const buildDefaultDocumentFormValues = (): DocumentFormValues => ({
  nd_number: '',
  em_number: '',
  title: '',
  work_package_id: '',
  work_unit_id: '',
  document_type_id: '',
  discipline_id: '',
  notes: '',
  target_value: '',
  qualifier: '',
  communication_user_ids: [],
  communication_include_project_members: true,
});

export const buildDefaultRevisionFormValues = (): RevisionFormValues => ({
  revision_name_id: 0,
  step: 1,
  start_date: getTodayDateString(),
  expected_issue_date: '',
});

export const buildDefaultRevisionWorkflowFormValues = (): RevisionWorkflowFormValues => ({
  author_party_id: undefined,
  checkers: [],
  approvers: [],
});

export interface DocumentFormValidationOptions {
  workUnitRequired?: boolean;
}

export const DOCUMENT_FORM_FIELD_LABELS: Record<string, string> = {
  document_type_id: 'Document type',
  discipline_id: 'Discipline',
  work_package_id: 'Work package',
  work_unit_id: 'Work unit',
  nd_number: 'ND number',
  title: 'Title',
  revision_name_id: 'Revision name',
  revision_step: 'Revision step',
  step: 'Revision step',
  checkers: 'Checkers',
  approvers: 'Approvers',
};

export const labelsForDocumentFields = (keys: string[]): string[] =>
  keys.map((key) => DOCUMENT_FORM_FIELD_LABELS[key] || key);

export const getDocumentFormMissingFields = (
  values: DocumentFormValues,
  options?: DocumentFormValidationOptions
): string[] => {
  const workUnitRequired = options?.workUnitRequired ?? true;
  const missing: string[] = [];
  if (!values.document_type_id) missing.push('document_type_id');
  if (!values.discipline_id) missing.push('discipline_id');
  if (!values.work_package_id) missing.push('work_package_id');
  if (workUnitRequired && !values.work_unit_id) missing.push('work_unit_id');
  if (!values.nd_number.trim()) missing.push('nd_number');
  if (!values.title.trim()) missing.push('title');
  return missing;
};

export const isDocumentFormValid = (
  values: DocumentFormValues,
  options?: DocumentFormValidationOptions
): boolean => getDocumentFormMissingFields(values, options).length === 0;

export const getRevisionFormMissingFields = (values: RevisionFormValues): string[] => {
  const missing: string[] = [];
  if (!(values.revision_name_id > 0)) missing.push('revision_name_id');
  if (values.step == null || values.step < 1 || values.step > 20) missing.push('step');
  return missing;
};

export const isRevisionFormValid = (values: RevisionFormValues): boolean =>
  getRevisionFormMissingFields(values).length === 0;

export const getRevisionWorkflowFormMissingFields = (values: RevisionWorkflowFormValues): string[] => {
  const hasOriginator = !!values.author_party_id;
  const hasCheckers = values.checkers.length > 0;
  const hasApprovers = values.approvers.length > 0;
  if (!hasOriginator && !hasCheckers && !hasApprovers) {
    return [];
  }
  const missing: string[] = [];
  if (!hasCheckers) missing.push('checkers');
  if (!hasApprovers) missing.push('approvers');
  return missing;
};

export const isRevisionWorkflowFormValid = (values: RevisionWorkflowFormValues): boolean =>
  getRevisionWorkflowFormMissingFields(values).length === 0;

export const hasWorkflowParties = (values: RevisionWorkflowFormValues): boolean =>
  Boolean(values.author_party_id) ||
  values.checkers.length > 0 ||
  values.approvers.length > 0;
